<template>
  <div>
    <ProgressLoader
      v-if="loading"
      mode="indeterminate"
      color="#5AC396"
      size="150px"
    />
    <div v-else>
      <div class="title">
        {{ $t("briefcase.ordersFrame.title") }}
      </div>
      <AuNoData
        v-if="transactions.length === 0"
        :text="$t('briefcase.ordersFrame.noData')"
      />
      <div v-else>
        <AuTable
          class="order-table"
          :columns="columns"
          :items="items"
        />
        <AuAccordion
          v-for="(transaction, index) in items"
          :key="transaction"
          :last-child="(index == items.length - 1)"
          class="order-accordion"
        >
          <template #title>
            <span class="company-header">{{ index + 1 }}</span>
          </template>
          <template #content>
            <div class="mobile-row">
              <div class="flex justify-between">
                <span class="name">
                  {{ $t("briefcase.ordersFrame.columnOrderId") }}
                </span>
                <span>
                  {{ transaction.orderId }}
                </span>
              </div>
              <div class="flex justify-between">
                <span class="name">
                  {{ $t("briefcase.ordersFrame.columnAmount") }}
                </span>
                <span>
                  {{ transaction.amount }}
                </span>
              </div>
              <div class="flex justify-between">
                <span class="name">
                  {{ $t("briefcase.ordersFrame.columnPair") }}
                </span>
                <span>
                  {{ transaction.name }}
                </span>
              </div>
              <div class="flex justify-between">
                <span class="name">
                  {{ $t("briefcase.ordersFrame.columnStatus") }}
                </span>
                <span>
                  {{ transaction.status }}
                </span>
              </div>
            </div>
          </template>
        </AuAccordion>
        <AuPagination
          v-if="totalItems > itemsPerPage"
          :total-items="totalItems"
          @pageChanged="pageChanged"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import AuAccordion from "@/components/extended/AuAccordion.vue";
import AuNoData from "@/components/extended/AuNoData.vue";
import AuPagination from "@/components/extended/AuPagination.vue";
import AuTable from "@/components/extended/AuTable.vue";
import AxiosController from "@/api/axiosController";
import BigNumber from "bignumber.js";
import i18n from "@/localization/localization.js";
import ProgressLoader from "@/loaders/progress-bar";
import { computed, onMounted, ref } from "vue";

const loading = ref(true);
const axiosController = new AxiosController();
const page = ref(1);
const itemsPerPage = ref(30);
const transactions = ref([]);
const totalItems = ref(0);

const columns = computed(() => {
  return [
    { name: i18n.global.t("briefcase.ordersFrame.columnOrderId"), align: "left", property: "orderId" },
    { name: i18n.global.t("briefcase.ordersFrame.columnAmount"), align: "center", property: "amount" },
    { name: i18n.global.t("briefcase.ordersFrame.columnPair"), align: "center", property: "name" },
    { name: i18n.global.t("briefcase.ordersFrame.columnStatus"), align: "center", property: "status" }
  ];
});

const items = computed(() => {
  return transactions.value.map(item => (
    {
      orderId: item?.exchangeOrder.orderId,
      amount: BigNumber(item.amount).dividedBy(BigNumber(10).pow(item.auToken.decimals)).toString(10),
      name: item?.exchangeOrder.name,
      status: item?.exchangeOrder.status
    }));
});

const pageChanged = async actualPage => {
  page.actualPage = actualPage;
  await getData();
};

const getData = async () => {
  loading.value = true;
  const params = {
    page: page.value,
    itemsPerPage: itemsPerPage.value,
    hasExchangeOrder: true
  };
  try {
    const resp = await axiosController.getTransactionsHistory(params);
    transactions.value = resp.data;
    totalItems.value = resp.totalItems;
  }
  catch (e) {
    console.error(e);
  }
  loading.value = false;
};

onMounted(async () => {
  await getData();
});

</script>

<style scoped lang="scss">
.title {
  font-size: 20px;
}
.order-table {
  @media screen and (max-width:500px) {
    display: none;
  }
}
.order-accordion {
  display: none;

  @media screen and (max-width:500px) {
    display: block;
  }
}
.mobile-row {
  display: flex;
  flex-direction: column;
  gap: 20px;

  font-weight: 450;
  font-size: 12px;

  .name {
    color: var(--color-60)
  }
}
</style>